import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../interceptors";
import { baseUrl } from "../../config/config";
import { jdToPdfBlob } from "../../utils/jdToPdf";
import { fetchSuggestedCandidates } from "../../utils/dummySuggestedCandidates";

const TONES = [
  { key: "professional", label: "Professional" },
  { key: "casual", label: "Casual" },
  { key: "startup", label: "Startup" },
  { key: "enterprise", label: "Enterprise" },
];

const HIRING_TYPES = ["Lateral", "Campus", "Regular"];

const STUB_LOCATIONS = [
  "Bengaluru",
  "Mumbai",
  "Delhi NCR",
  "Pune",
  "Hyderabad",
  "Chennai",
  "Remote",
  "Hybrid",
];

const STUB_RECRUITERS = ["Auto-assign", "Priya M", "Arun K", "Sneha R"];

// Mirrors src/screens/admin/source-hub/source-hub.jsx so the wizard's
// Refresh-source tab speaks the same language as the standalone screen.
const PROVIDER_COLORS = {
  NAUKRI: "#FF7B1C",
  LINKEDIN: "#0A66C2",
  FOUNDIT: "#E94235",
  INDEED: "#2164F3",
  APNA: "#0066FF",
  AFFINDA: "#7C3AED",
  MANUAL: "#94A3B8",
  CSV: "#64748B",
};
const PROVIDER_FILTERS = [
  "ALL",
  "NAUKRI",
  "LINKEDIN",
  "FOUNDIT",
  "INDEED",
  "APNA",
];
const NOTICE_PERIODS = [
  "Immediate",
  "15 Days",
  "30 Days",
  "60 Days",
  "90 Days",
];

const generateJD = ({ title, hiringType, locations, tone, openings }) => {
  const t = title || "this role";
  const loc = locations && locations.length ? locations.join(", ") : "Remote";
  const intro =
    {
      professional: `We are seeking ${openings || 1} ${t} to join our high-performing team in ${loc}.`,
      casual: `We're looking for ${openings || 1} amazing ${t} to come build with us in ${loc}.`,
      startup: `We need a scrappy ${t} who can move fast and ship in ${loc}.`,
      enterprise: `Our enterprise team is hiring ${openings || 1} ${t} for our ${loc} office to drive strategic initiatives.`,
    }[tone] || "";
  return {
    title: t,
    overview: intro,
    responsibilities: [
      `Own end-to-end delivery of ${t.toLowerCase()} workstreams.`,
      `Partner with cross-functional teams to translate requirements into shippable features.`,
      `Mentor peers and raise the technical bar through code review.`,
      `Drive measurable improvements in performance, quality, and reliability.`,
    ],
    requirements: [
      `${hiringType === "Campus" ? "Recent graduate" : "5+ years"} of relevant experience for ${t.toLowerCase()}.`,
      `Strong fundamentals in your domain plus a portfolio of shipped work.`,
      `Excellent written and verbal communication.`,
      `Bonus: experience working in fast-moving teams.`,
    ],
    perks: [
      "Competitive compensation + equity",
      "Comprehensive health + wellness benefits",
      "Learning stipend and conference budget",
      "Remote-friendly with quarterly meet-ups",
    ],
  };
};

const wordCount = (jd) => {
  if (!jd) return 0;
  const all = [
    jd.overview,
    ...(jd.responsibilities || []),
    ...(jd.requirements || []),
    ...(jd.perks || []),
  ].join(" ");
  return all.split(/\s+/).filter(Boolean).length;
};

// initialForm lets callers (e.g. the Jobs page's "+ New via AI" flow) pre-fill
// the Basics step from /ai/jobs/from-prompt. Unset keys fall back to defaults.
const buildInitialForm = (init) => ({
  title: init?.title || "",
  jobDescription: init?.jobDescription || "",
  openings:
    typeof init?.openings === "number" && init.openings > 0 ? init.openings : 1,
  hiringType: init?.hiringType || "Lateral",
  locations: Array.isArray(init?.locations) ? init.locations : [],
  recruiter: init?.recruiter || "Auto-assign",
  closeDate: init?.closeDate || "",
  tone: init?.tone || "professional",
  // advanced
  minCtc: init?.minCtc || "",
  maxCtc: init?.maxCtc || "",
  minimumExperience: init?.minimumExperience || "",
  maximumExperience: init?.maximumExperience || "",
  vacancyStartDate: init?.vacancyStartDate || "",
  vacancyApprovalDate: init?.vacancyApprovalDate || "",
  closureTargetDate: init?.closureTargetDate || "",
  // CSV strings — split on "," at publish time into List<String>.
  domainSkills: init?.domainSkills || "",
  softSkills: init?.softSkills || "",
  interviewRounds: init?.interviewRounds || "",
  interviewers: init?.interviewers || "",
  hiringOperations: init?.hiringOperations || "",
});

const CreateJobWizard = ({ open, onClose, initialForm }) => {
  const [step, setStep] = useState("basics");
  const [form, setForm] = useState(() => buildInitialForm(initialForm));
  const [customLocations, setCustomLocations] = useState([]);
  const [jd, setJd] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [createdJobId, setCreatedJobId] = useState(null);

  // step 3 state
  const [suggested, setSuggested] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [skillFilter, setSkillFilter] = useState(new Set());
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(null);
  // Pagination for the suggestions API — page is 0-based to match the backend.
  const SUGGESTIONS_PAGE_SIZE = 10;
  const [pageIndex, setPageIndex] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    // Re-seed on every open so the wizard reflects the current initialForm
    // (or defaults). The previous version reset on close, which left stale
    // state if initialForm changed between opens.
    if (open) {
      setStep("basics");
      setForm(buildInitialForm(initialForm));
      setCustomLocations([]);
      setJd(null);
      setGenerating(false);
      setPublishing(false);
      setPublishError(null);
      setCreatedJobId(null);
      setSuggested([]);
      setSelectedIds(new Set());
      setSkillFilter(new Set());
      setSearch("");
      setSendError(null);
      setSendSuccess(null);
      setPageIndex(0);
      setTotalElements(0);
      setTotalPages(0);
    }
    // initialForm is intentionally read only at the open transition; if it
    // were in the deps, a stable parent reference vs. fresh object would
    // re-trigger this effect mid-session and wipe user edits.
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load suggested candidates whenever we land on the candidates step or
  // change page. `selectedIds` is intentionally preserved across pages so the
  // user can accumulate a selection set while paging.
  useEffect(() => {
    if (step !== "candidates") return;
    let cancelled = false;
    setLoadingSuggested(true);
    (async () => {
      try {
        const result = await fetchSuggestedCandidates(createdJobId, form, {
          page: pageIndex,
          size: SUGGESTIONS_PAGE_SIZE,
        });
        if (cancelled) return;
        setSuggested(result.content);
        setTotalElements(result.totalElements);
        setTotalPages(result.totalPages);
      } finally {
        if (!cancelled) setLoadingSuggested(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, pageIndex, createdJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLocation = (loc) =>
    setForm((f) => ({
      ...f,
      locations: f.locations.includes(loc)
        ? f.locations.filter((l) => l !== loc)
        : [...f.locations, loc],
    }));
  const addCustomLocation = (raw) => {
    const loc = (raw || "").trim();
    if (!loc) return;
    const known = [...STUB_LOCATIONS, ...customLocations].map((s) =>
      s.toLowerCase(),
    );
    if (!known.includes(loc.toLowerCase())) {
      setCustomLocations((prev) => [...prev, loc]);
    }
    setForm((f) =>
      f.locations.includes(loc)
        ? f
        : { ...f, locations: [...f.locations, loc] },
    );
  };

  const buildJdPayload = (f) => ({
    title: f.title,
    hiringType: f.hiringType,
    locations: f.locations,
    tone: f.tone,
    openings: Number(f.openings) || 1,
    minimumExperience: f.minimumExperience,
    maximumExperience: f.maximumExperience,
    domainSkills: f.domainSkills,
    softSkills: f.softSkills,
  });

  const fetchJdFromApi = async (f) => {
    const { data } = await axiosInstance.post(
      "/ai/jobs/generate-jd",
      buildJdPayload(f),
    );
    // Guard against partial responses — fall back if any required field is missing.
    if (
      !data ||
      !data.overview ||
      !Array.isArray(data.responsibilities) ||
      !Array.isArray(data.requirements) ||
      !Array.isArray(data.perks)
    ) {
      throw new Error("Invalid JD response shape");
    }
    return data;
  };

  const generate = async () => {
    if (!form.title.trim()) return;
    setGenerating(true);
    try {
      const jdData = await fetchJdFromApi(form);
      setJd(jdData);
    } catch (err) {
      console.warn("LLM JD generation failed, using template fallback:", err);
      setJd(generateJD(form));
    } finally {
      setGenerating(false);
      setStep("preview");
    }
  };

  const regenerate = async () => {
    if (!form.title.trim()) return;
    setGenerating(true);
    try {
      const jdData = await fetchJdFromApi(form);
      setJd(jdData);
    } catch (err) {
      console.warn("LLM JD regeneration failed, using template fallback:", err);
      setJd(generateJD(form));
    } finally {
      setGenerating(false);
    }
  };

  const publish = async () => {
    if (!jd || publishing) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const fd = new FormData();
      fd.append("positionName", form.title);
      fd.append("positionCounts", String(Number(form.openings) || 1));
      fd.append(
        "hiringType",
        (form.hiringType || "lateral").toLowerCase() + "_hiring",
      );
      fd.append("locations", JSON.stringify(form.locations || []));
      fd.append("jobDescriptionMedia", jdToPdfBlob(jd));
      fd.append("jdJson", JSON.stringify(jd));
      fd.append("userId", String(localStorage.getItem("userId")));
      const orgId = localStorage.getItem("organizationId");
      if (orgId) fd.append("orgId", String(orgId));

      // Recruiter selection (skip the "Auto-assign" placeholder).
      if (form.recruiter && form.recruiter !== "Auto-assign") {
        fd.append("recruiterName", JSON.stringify([form.recruiter]));
      }

      // Map close date → vacancyClosureDate; pass advanced dates as-is (yyyy-MM-dd).
      if (form.closeDate) fd.append("vacancyClosureDate", form.closeDate);
      if (form.vacancyStartDate)
        fd.append("vacancyStartDate", form.vacancyStartDate);
      if (form.vacancyApprovalDate)
        fd.append("vacancyApprovalDate", form.vacancyApprovalDate);
      if (form.closureTargetDate)
        fd.append("closureTargetDate", form.closureTargetDate);

      // Compensation & experience are typed as String in the DTO.
      if (form.minCtc) fd.append("minCtc", String(form.minCtc));
      if (form.maxCtc) fd.append("maxCtc", String(form.maxCtc));
      if (form.minimumExperience)
        fd.append("minimumExperience", String(form.minimumExperience));
      if (form.maximumExperience)
        fd.append("maximumExperience", String(form.maximumExperience));

      // List<String> fields — split CSV input on "," and JSON-stringify the array,
      // matching the convention used for `locations`.
      const csvToList = (s) =>
        (s || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);

      const domainList = csvToList(form.domainSkills);
      const softList = csvToList(form.softSkills);
      const roundsList = csvToList(form.interviewRounds);
      const interviewersList = csvToList(form.interviewers);

      if (domainList.length)
        fd.append("domainSkills", JSON.stringify(domainList));
      if (softList.length) fd.append("softSkills", JSON.stringify(softList));
      if (roundsList.length)
        fd.append("interviewRounds", JSON.stringify(roundsList));
      if (interviewersList.length)
        fd.append("interviewers", JSON.stringify(interviewersList));

      if (form.hiringOperations)
        fd.append("hiringOperations", form.hiringOperations);

      // jobDescription is List<String> on the backend — split user text by
      // newlines so each non-empty line becomes one list item.
      if (form.jobDescription?.trim()) {
        const lines = form.jobDescription
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);
        fd.append("jobDescription", JSON.stringify(lines));
      }

      const res = await axiosInstance.post(
        `${baseUrl}/job-posting/job-details/save`,
        fd,
      );
      const jobId = res?.data?.data?.jobId ?? null;
      setCreatedJobId(jobId);
      // Land on the Source step — suggestions are loaded when we leave it.
      setStep("source");
    } catch (e) {
      setPublishError(
        e?.response?.data?.message || e?.message || "Publish failed.",
      );
    } finally {
      setPublishing(false);
    }
  };

  const allSkills = useMemo(() => {
    const s = new Set();
    suggested.forEach((c) => (c.skills || []).forEach((k) => s.add(k)));
    return Array.from(s).sort();
  }, [suggested]);

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return suggested.filter((c) => {
      if (q && !`${c.name} ${c.email}`.toLowerCase().includes(q)) return false;
      if (skillFilter.size === 0) return true;
      return (c.skills || []).some((k) => skillFilter.has(k));
    });
  }, [suggested, search, skillFilter]);

  const toggleSkillFilter = (s) =>
    setSkillFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const toggleCandidate = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allFilteredSelected =
    filteredCandidates.length > 0 &&
    filteredCandidates.every((c) => selectedIds.has(c.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredCandidates.forEach((c) => next.delete(c.id));
      } else {
        filteredCandidates.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  const sendInvites = async () => {
    if (sending) return;
    const chosen = suggested.filter((c) => selectedIds.has(c.id));
    if (chosen.length === 0) return;
    setSending(true);
    setSendError(null);
    setSendSuccess(null);
    try {
      const payload = {
        jobId: createdJobId != null ? Number(createdJobId) : null,
        interviewId: null,
        templateId: "interview-acceptance",
        subjectOverride: null,
        bodyOverride: null,
        templateVarsOverride: null,
        candidates: chosen.map((c) => ({
          email: c.email,
          candidateName: c.name,
          ...(c.phone ? { phone: c.phone } : {}),
        })),
      };
      console.log(payload);
      const r = await axiosInstance.post(
        "/job-posting/interview-acceptance/send",
        payload,
      );
      const sent = r.data?.sent ?? chosen.length;
      setSendSuccess(
        `Invites sent to ${sent} candidate${sent === 1 ? "" : "s"}.`,
      );
      setTimeout(() => onClose?.(), 1500);
    } catch (e) {
      setSendError(
        e?.response?.data?.message || e?.message || "Failed to send invites.",
      );
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ background: "rgba(15,23,42,0.45)", padding: 24 }}
        onClick={onClose}
      >
        <motion.div
          key="panel"
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
          style={panelStyle}
        >
          <Header step={step} onClose={onClose} publishing={publishing} />

          <div style={bodyStyle}>
            {step === "basics" && (
              <BasicsStep
                form={form}
                update={update}
                toggleLocation={toggleLocation}
                customLocations={customLocations}
                addCustomLocation={addCustomLocation}
                generating={generating}
                onGenerate={generate}
              />
            )}
            {step === "preview" && (
              <PreviewStep
                jd={jd}
                generating={generating}
                publishing={publishing}
                publishError={publishError}
                onRegenerate={regenerate}
                onBack={() => setStep("basics")}
                onPublish={publish}
              />
            )}
            {step === "source" && (
              <SourceStep
                jobId={createdJobId}
                onBack={() => setStep("preview")}
                onContinue={() => setStep("candidates")}
              />
            )}
            {step === "candidates" && (
              <CandidatesStep
                loading={loadingSuggested}
                candidates={filteredCandidates}
                totalCount={totalElements}
                allSkills={allSkills}
                skillFilter={skillFilter}
                toggleSkillFilter={toggleSkillFilter}
                search={search}
                setSearch={setSearch}
                selectedIds={selectedIds}
                toggleCandidate={toggleCandidate}
                allFilteredSelected={allFilteredSelected}
                toggleSelectAll={toggleSelectAll}
                sending={sending}
                sendError={sendError}
                sendSuccess={sendSuccess}
                onCancel={onClose}
                onSend={sendInvites}
                pageIndex={pageIndex}
                totalPages={totalPages}
                pageSize={SUGGESTIONS_PAGE_SIZE}
                onPrevPage={() => setPageIndex((p) => Math.max(0, p - 1))}
                onNextPage={() =>
                  setPageIndex((p) =>
                    totalPages > 0 ? Math.min(totalPages - 1, p + 1) : p,
                  )
                }
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ── header / stepper ── */
const STEP_ORDER = ["basics", "preview", "source", "candidates"];
const Header = ({ step, onClose, publishing }) => {
  const stepIdx = Math.max(0, STEP_ORDER.indexOf(step));
  const labels = ["Basics", "Preview JD", "Source", "Suggested candidates"];
  return (
    <div style={hdrStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h2
          style={{ fontSize: 16, fontWeight: 700, color: "#0F172A", margin: 0 }}
        >
          Create job
        </h2>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {labels.map((label, i) => (
            <div
              key={label}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  background: i <= stepIdx ? "#7B5AC4" : "#E2E8F0",
                  color: i <= stepIdx ? "white" : "#64748B",
                }}
              >
                {i + 1}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: i === stepIdx ? "#0F172A" : "#94A3B8",
                }}
              >
                {label}
              </span>
              {i < labels.length - 1 && (
                <span style={{ width: 18, height: 1, background: "#E2E8F0" }} />
              )}
            </div>
          ))}
        </div>
      </div>
      <button
        onClick={onClose}
        disabled={publishing}
        style={{
          background: "transparent",
          border: "none",
          fontSize: 22,
          cursor: publishing ? "not-allowed" : "pointer",
          color: "#64748B",
          lineHeight: 1,
          padding: 0,
        }}
        aria-label="Close"
      >
        ×
      </button>
    </div>
  );
};

/* ── step 1: basics ── */
const BasicsStep = ({
  form,
  update,
  toggleLocation,
  customLocations,
  addCustomLocation,
  generating,
  onGenerate,
}) => {
  const [addingLoc, setAddingLoc] = useState(false);
  const [newLoc, setNewLoc] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);

  const commitNewLoc = () => {
    addCustomLocation(newLoc);
    setNewLoc("");
    setAddingLoc(false);
  };

  const allLocationOptions = [...STUB_LOCATIONS, ...customLocations];

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <Field label="Job title">
        <input
          value={form.title}
          onChange={(e) => update("title", e.target.value)}
          placeholder="e.g. Senior Backend Engineer"
          style={input}
        />
      </Field>
      <Field label="Job description">
        <textarea
          value={form.jobDescription}
          onChange={(e) => update("jobDescription", e.target.value)}
          placeholder="Briefly describe the role. Each new line will be sent as a separate item."
          rows={4}
          style={{ ...input, resize: "vertical", minHeight: 90 }}
        />
      </Field>
      <Row>
        <Field label="Openings">
          <input
            type="number"
            min="1"
            value={form.openings}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                update("openings", "");
                return;
              }
              const parsed = parseInt(raw, 10);
              update("openings", Number.isNaN(parsed) ? "" : parsed);
            }}
            onBlur={(e) => {
              const parsed = parseInt(e.target.value, 10);
              update(
                "openings",
                Number.isNaN(parsed) || parsed < 1 ? 1 : parsed,
              );
            }}
            style={input}
          />
        </Field>
        <Field label="Type">
          <select
            value={form.hiringType}
            onChange={(e) => update("hiringType", e.target.value)}
            style={input}
          >
            {HIRING_TYPES.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </Field>
      </Row>
      <Field label="Locations (multi-select)">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {allLocationOptions.map((l) => {
            const active = form.locations.includes(l);
            return (
              <button
                key={l}
                onClick={() => toggleLocation(l)}
                style={chip(active)}
              >
                {l}
              </button>
            );
          })}
          {addingLoc ? (
            <span
              style={{ display: "inline-flex", gap: 4, alignItems: "center" }}
            >
              <input
                autoFocus
                value={newLoc}
                onChange={(e) => setNewLoc(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitNewLoc();
                  if (e.key === "Escape") {
                    setNewLoc("");
                    setAddingLoc(false);
                  }
                }}
                placeholder="Add location"
                style={{
                  ...input,
                  width: 140,
                  padding: "4px 8px",
                  fontSize: 11,
                  height: 26,
                }}
              />
              <button
                onClick={commitNewLoc}
                style={{ ...chip(true), padding: "4px 10px" }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setNewLoc("");
                  setAddingLoc(false);
                }}
                style={{ ...chip(false), padding: "4px 10px" }}
              >
                ×
              </button>
            </span>
          ) : (
            <button
              onClick={() => setAddingLoc(true)}
              style={{
                ...chip(false),
                borderStyle: "dashed",
                color: "#7B5AC4",
                borderColor: "#C7D2FE",
              }}
              title="Add a custom location"
            >
              + Add
            </button>
          )}
        </div>
      </Field>
      <Row>
        <Field label="Recruiter">
          <select
            value={form.recruiter}
            onChange={(e) => update("recruiter", e.target.value)}
            style={input}
          >
            {STUB_RECRUITERS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Close date">
          <input
            type="date"
            value={form.closeDate}
            onChange={(e) => update("closeDate", e.target.value)}
            style={input}
          />
        </Field>
      </Row>
      <Field label="JD tone">
        <div style={{ display: "flex", gap: 6 }}>
          {TONES.map((t) => {
            const active = form.tone === t.key;
            return (
              <button
                key={t.key}
                onClick={() => update("tone", t.key)}
                style={{ ...chip(active), flex: 1 }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </Field>

      {/* ── More options (collapsible) ───────────────────────────── */}
      <div style={moreOptionsBox}>
        <button
          onClick={() => setMoreOpen((v) => !v)}
          style={moreToggle}
          aria-expanded={moreOpen}
        >
          <span style={{ marginRight: 6 }}>{moreOpen ? "▾" : "▸"}</span>
          {moreOpen ? "Hide" : "Show"} more options
        </button>
        {moreOpen && (
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <Row>
              <Field label="Min CTC">
                <input
                  value={form.minCtc}
                  onChange={(e) => update("minCtc", e.target.value)}
                  placeholder="e.g. 12 LPA"
                  style={input}
                />
              </Field>
              <Field label="Max CTC">
                <input
                  value={form.maxCtc}
                  onChange={(e) => update("maxCtc", e.target.value)}
                  placeholder="e.g. 24 LPA"
                  style={input}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Minimum experience">
                <input
                  value={form.minimumExperience}
                  onChange={(e) => update("minimumExperience", e.target.value)}
                  placeholder="e.g. 3 years"
                  style={input}
                />
              </Field>
              <Field label="Maximum experience">
                <input
                  value={form.maximumExperience}
                  onChange={(e) => update("maximumExperience", e.target.value)}
                  placeholder="e.g. 8 years"
                  style={input}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Vacancy start date">
                <input
                  type="date"
                  value={form.vacancyStartDate}
                  onChange={(e) => update("vacancyStartDate", e.target.value)}
                  style={input}
                />
              </Field>
              <Field label="Vacancy approval date">
                <input
                  type="date"
                  value={form.vacancyApprovalDate}
                  onChange={(e) =>
                    update("vacancyApprovalDate", e.target.value)
                  }
                  style={input}
                />
              </Field>
            </Row>
            <Field label="Closure target date">
              <input
                type="date"
                value={form.closureTargetDate}
                onChange={(e) => update("closureTargetDate", e.target.value)}
                style={input}
              />
            </Field>
            <CsvField
              label="Domain skills"
              value={form.domainSkills}
              onChange={(v) => update("domainSkills", v)}
              placeholder="React, TypeScript, Node.js"
              example="React, TypeScript, Node.js"
            />
            <CsvField
              label="Soft skills"
              value={form.softSkills}
              onChange={(v) => update("softSkills", v)}
              placeholder="Communication, Leadership, Ownership"
              example="Communication, Leadership, Ownership"
            />
            <CsvField
              label="Interview rounds"
              value={form.interviewRounds}
              onChange={(v) => update("interviewRounds", v)}
              placeholder="Tech Screen, System Design, HR"
              example="Tech Screen, System Design, HR"
            />
            <CsvField
              label="Interviewers"
              value={form.interviewers}
              onChange={(v) => update("interviewers", v)}
              placeholder="Priya M, Arun K, Sneha R"
              example="Priya M, Arun K, Sneha R"
            />
            <Field label="Hiring operations">
              <textarea
                value={form.hiringOperations}
                onChange={(e) => update("hiringOperations", e.target.value)}
                placeholder="Notes for the hiring ops team"
                rows={2}
                style={{ ...input, resize: "vertical", minHeight: 60 }}
              />
            </Field>
          </div>
        )}
      </div>

      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}
      >
        <button
          onClick={onGenerate}
          disabled={generating || !form.title.trim()}
          style={{
            ...btnPrimary,
            opacity: generating || !form.title.trim() ? 0.5 : 1,
            cursor:
              generating || !form.title.trim() ? "not-allowed" : "pointer",
          }}
        >
          {generating ? "Generating…" : "Generate JD with AI →"}
        </button>
      </div>
    </div>
  );
};

/* ── CsvField: text input with an info-icon tooltip explaining CSV format.
   Values are typed comma-separated; we split on "," at publish time. ── */
const CsvField = ({ label, value, onChange, placeholder, example }) => {
  const tooltip = `Enter values separated by commas.${example ? `\nExample: ${example}` : ""}`;
  return (
    <div style={{ marginBottom: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 10,
            color: "#64748B",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </span>
        <span
          title={tooltip}
          aria-label={tooltip}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            borderRadius: 999,
            background: "#E2E8F0",
            color: "#475569",
            fontSize: 9,
            fontWeight: 700,
            cursor: "help",
            fontStyle: "italic",
            fontFamily: "Georgia, serif",
          }}
        >
          i
        </span>
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={input}
      />
    </div>
  );
};

/* ── step 2: preview ── */
const PreviewStep = ({
  jd,
  generating,
  publishing,
  publishError,
  onRegenerate,
  onBack,
  onPublish,
}) => (
  <div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 10,
      }}
    >
      <div style={cardLabel}>AI-generated job description</div>
      {jd && (
        <span style={{ fontSize: 11, color: "#64748B" }}>
          {wordCount(jd)} words
        </span>
      )}
    </div>

    {generating && (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "#64748B",
          fontSize: 12,
        }}
      >
        Re-drafting with AI…
      </div>
    )}

    {jd && !generating && (
      <div style={previewBox}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0F172A",
            marginBottom: 6,
          }}
        >
          {jd.title}
        </div>
        <div
          style={{
            fontStyle: "italic",
            fontSize: 12,
            color: "#475569",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          {jd.overview}
        </div>

        <Section heading="Responsibilities" items={jd.responsibilities} />
        <Section heading="Requirements" items={jd.requirements} />
        <Section heading="Perks" items={jd.perks} />
      </div>
    )}

    {publishError && <div style={errorBox}>{publishError}</div>}

    <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
      <button onClick={onBack} disabled={publishing} style={btnGhost}>
        ← Back
      </button>
      <button
        onClick={onRegenerate}
        disabled={generating || publishing}
        style={btnGhost}
      >
        Regenerate
      </button>
      <button
        onClick={onPublish}
        disabled={publishing || !jd}
        style={{
          ...btnPrimary,
          marginLeft: "auto",
          opacity: publishing || !jd ? 0.6 : 1,
          cursor: publishing || !jd ? "not-allowed" : "pointer",
        }}
      >
        {publishing ? "Publishing…" : "Publish job →"}
      </button>
    </div>
  </div>
);

/* ── step 3: source candidates (refresh / upload / talent pool) ── */
const SourceStep = ({ jobId, onBack, onContinue }) => {
  const [tab, setTab] = useState("refresh");
  // Counters surfaced in the footer so the user knows what they brought in.
  const [imported, setImported] = useState(0);
  const [uploaded, setUploaded] = useState(0);
  const [poolsPicked, setPoolsPicked] = useState(0);

  const total = imported + uploaded + poolsPicked;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
          Source candidates
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
          Optional — pull candidates from external boards, upload resumes, or
          pick from an existing talent pool before reviewing AI suggestions.
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 1,
          borderBottom: "1px solid #E2E8F0",
          marginBottom: 12,
        }}
      >
        {[
          { id: "refresh", label: "Refresh source" },
          { id: "upload", label: "Upload resumes" },
          { id: "pool", label: "Talent pool" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={subTabStyle(tab === t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ minHeight: 240 }}>
        {tab === "refresh" && (
          <RefreshSourceTab onImported={(n) => setImported((x) => x + n)} />
        )}
        {tab === "upload" && (
          <UploadResumesTab onParsed={(n) => setUploaded((x) => x + n)} />
        )}
        {tab === "pool" && (
          <TalentPoolTab jobId={jobId} onPickedChange={setPoolsPicked} />
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 14,
          alignItems: "center",
        }}
      >
        <button onClick={onBack} style={btnGhost}>
          ← Back
        </button>
        <span style={{ fontSize: 11, color: "#64748B" }}>
          {total > 0
            ? `${imported} imported · ${uploaded} uploaded · ${poolsPicked} pool${poolsPicked === 1 ? "" : "s"} picked`
            : "Nothing sourced yet — you can skip this step."}
        </span>
        <button
          onClick={onContinue}
          style={{ ...btnPrimary, marginLeft: "auto" }}
        >
          Continue to suggestions →
        </button>
      </div>
    </div>
  );
};

/* Refresh source — search /common/sources/search, filter, multi-select, import.
   Mirrors the filter & selection UX from src/screens/admin/source-hub/source-hub.jsx. */
const RefreshSourceTab = ({ onImported }) => {
  const [connections, setConnections] = useState([]);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({
    provider: "ALL",
    minExp: "",
    maxExp: "",
    location: "",
    minCTC: "",
    maxCTC: "",
    notice: "",
    skills: "",
  });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const tenantId = localStorage.getItem("tenantId");
    (async () => {
      try {
        const r = await axiosInstance.get(
          `/common/sources/connections?tenantId=${tenantId || ""}`,
        );
        setConnections(r.data || []);
      } catch {
        setConnections([]);
      }
    })();
  }, []);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const doSearch = async () => {
    setSearching(true);
    setMsg(null);
    try {
      const r = await axiosInstance.post("/common/sources/search", {
        keywords: query,
        location: filters.location || null,
        minExperience: filters.minExp ? Number(filters.minExp) : null,
        maxExperience: filters.maxExp ? Number(filters.maxExp) : null,
        minCTC: filters.minCTC || null,
        maxCTC: filters.maxCTC || null,
        noticePeriod: filters.notice || null,
        skills: filters.skills
          ? filters.skills
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
        provider: filters.provider === "ALL" ? null : filters.provider,
        page: 1,
        pageSize: 25,
      });
      setResults(r.data?.candidates || []);
      setTotalCount(r.data?.totalCount ?? (r.data?.candidates?.length || 0));
      setSelected(new Set());
    } catch (e) {
      setMsg(e?.message || "Search failed.");
      setResults([]);
      setTotalCount(0);
    } finally {
      setSearching(false);
    }
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const buildPayload = (ids) =>
    results
      .filter((c) => ids.includes(c.externalId))
      .map((c) => ({
        provider: c.provider,
        externalId: c.externalId,
        email: c.email,
        phone: c.phone,
        firstName: c.firstName,
        lastName: c.lastName,
        company: c.currentCompany,
        title: c.currentTitle,
        location: c.location,
      }));

  const importIds = async (ids) => {
    if (!ids.length) return;
    setImporting(true);
    setMsg(null);
    try {
      const r = await axiosInstance.post("/common/sources/search/import", {
        candidates: buildPayload(ids),
        tenantId: Number(localStorage.getItem("tenantId")) || 1,
      });
      const imported = r.data?.imported || 0;
      setMsg(`Imported ${imported}, ${r.data?.duplicates || 0} duplicates.`);
      if (imported) onImported?.(imported);
      setSelected(new Set());
    } catch (e) {
      setMsg(e?.message || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      {/* Connections strip */}
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}
      >
        {connections.length === 0 ? (
          <span style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>
            No sources connected yet — wire up Naukri, LinkedIn, etc. from
            <code style={{ marginLeft: 4 }}>/admin/source-hub</code>.
          </span>
        ) : (
          connections.map((c) => (
            <span key={c.id || c.provider} style={connectionPill}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: c.status === "CONNECTED" ? "#10B981" : "#CBD5E1",
                  display: "inline-block",
                }}
                title={c.status}
              />
              <span
                style={{
                  color: PROVIDER_COLORS[c.provider] || "#475569",
                  fontWeight: 600,
                }}
              >
                {c.provider}
              </span>
              {c.displayName && (
                <span style={{ color: "#94A3B8" }}>· {c.displayName}</span>
              )}
            </span>
          ))
        )}
      </div>

      {/* Search bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doSearch()}
          placeholder="Search by skills, title, location…"
          style={{ ...input, flex: 1 }}
        />
        <button
          onClick={doSearch}
          disabled={searching}
          style={{ ...btnPrimary, opacity: searching ? 0.6 : 1 }}
        >
          {searching ? "Searching…" : "Search"}
        </button>
      </div>

      {/* Filter bar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        {PROVIDER_FILTERS.map((p) => (
          <button
            key={p}
            onClick={() => setF("provider", p)}
            style={chip(filters.provider === p)}
          >
            {p === "ALL" ? "All sources" : p}
          </button>
        ))}
        <input
          value={filters.minExp}
          onChange={(e) => setF("minExp", e.target.value)}
          placeholder="Min exp"
          style={{ ...input, width: 70, padding: "5px 8px", fontSize: 11 }}
        />
        <span style={{ fontSize: 11, color: "#94A3B8" }}>–</span>
        <input
          value={filters.maxExp}
          onChange={(e) => setF("maxExp", e.target.value)}
          placeholder="Max"
          style={{ ...input, width: 60, padding: "5px 8px", fontSize: 11 }}
        />
        <input
          value={filters.location}
          onChange={(e) => setF("location", e.target.value)}
          placeholder="Location"
          style={{ ...input, width: 120, padding: "5px 8px", fontSize: 11 }}
        />
        <select
          value={filters.notice}
          onChange={(e) => setF("notice", e.target.value)}
          style={{ ...input, width: 130, padding: "5px 8px", fontSize: 11 }}
        >
          <option value="">Notice period</option>
          {NOTICE_PERIODS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <input
          value={filters.skills}
          onChange={(e) => setF("skills", e.target.value)}
          placeholder="Skills (comma-sep)"
          style={{ ...input, width: 170, padding: "5px 8px", fontSize: 11 }}
        />
        <input
          value={filters.minCTC}
          onChange={(e) => setF("minCTC", e.target.value)}
          placeholder="Min CTC"
          style={{ ...input, width: 80, padding: "5px 8px", fontSize: 11 }}
        />
        <input
          value={filters.maxCTC}
          onChange={(e) => setF("maxCTC", e.target.value)}
          placeholder="Max CTC"
          style={{ ...input, width: 80, padding: "5px 8px", fontSize: 11 }}
        />
      </div>

      {/* Bulk action bar — appears when there's a selection */}
      {selected.size > 0 && (
        <div style={bulkActionBar}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#4338CA" }}>
            {selected.size} selected
          </span>
          <button
            onClick={() => importIds([...selected])}
            disabled={importing}
            style={{ ...btnPrimary, padding: "5px 12px", fontSize: 11 }}
          >
            {importing ? "Importing…" : `Import ${selected.size} candidates`}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 11,
              color: "#64748B",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      )}

      {msg && (
        <div style={{ ...successBox, marginTop: 0, marginBottom: 8 }}>
          {msg}
        </div>
      )}

      {/* Result count */}
      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 6 }}>
        {totalCount > 0 ? `${totalCount} candidates found` : ""}
      </div>

      {/* Results */}
      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          border: "1px solid #E2E8F0",
          borderRadius: 6,
        }}
      >
        {results.length === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#94A3B8",
              fontSize: 12,
            }}
          >
            {searching
              ? "Searching…"
              : "Search across Naukri, Foundit, LinkedIn, and more — enter keywords above."}
          </div>
        ) : (
          results.map((c) => {
            const id = c.externalId;
            const checked = selected.has(id);
            const initials =
              `${c.firstName?.[0] || ""}${c.lastName?.[0] || ""}` || "?";
            const score = c.matchScore;
            const scoreColor =
              score >= 80 ? "#10B981" : score >= 60 ? "#F59E0B" : "#EF4444";
            return (
              <div
                key={id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderBottom: "1px solid #F1F5F9",
                  background: checked ? "#EEF2FF" : "white",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(id)}
                />
                <div style={avatarStyle}>{initials.toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#0F172A",
                      }}
                    >
                      {c.firstName} {c.lastName}
                    </span>
                    {c.provider && (
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          padding: "1px 6px",
                          borderRadius: 4,
                          color: "white",
                          background: PROVIDER_COLORS[c.provider] || "#94A3B8",
                        }}
                      >
                        {c.provider}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>
                    {[c.currentTitle, c.currentCompany]
                      .filter(Boolean)
                      .join(" @ ")}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      marginTop: 4,
                    }}
                  >
                    {c.experienceYears != null && (
                      <span style={badgeMuted}>{c.experienceYears}y</span>
                    )}
                    {c.location && <span style={badgeMuted}>{c.location}</span>}
                    {c.currentCTC && (
                      <span style={{ fontSize: 10, color: "#64748B" }}>
                        {c.currentCTC} → {c.expectedCTC}
                      </span>
                    )}
                    {c.noticePeriod && (
                      <span style={badgeAmber}>{c.noticePeriod}</span>
                    )}
                    {(c.skills || []).slice(0, 5).map((s) => (
                      <span key={s} style={badgeIndigo}>
                        {s}
                      </span>
                    ))}
                    {(c.skills || []).length > 5 && (
                      <span style={{ fontSize: 10, color: "#94A3B8" }}>
                        +{c.skills.length - 5}
                      </span>
                    )}
                  </div>
                </div>
                {typeof score === "number" && (
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      border: `2px solid ${scoreColor}`,
                      fontSize: 10,
                      fontWeight: 700,
                      color: scoreColor,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {score}
                  </span>
                )}
                <button
                  onClick={() => importIds([id])}
                  disabled={importing}
                  style={{ ...btnGhost, padding: "4px 10px", fontSize: 11 }}
                >
                  Import
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

/* Upload resumes — single multipart batch to /ai/resume-parse/upload, then
   poll /ai/resume-parse/batches/{batchId} every 8s until the batch is
   terminal. Mirrors the flow in src/screens/admin/upload-resumes/upload-resumes.jsx. */
const RESUME_API = "/ai/resume-parse";
const POLL_INTERVAL_MS = 8000;
const POLL_RETRY_MS = 5000;
const MAX_FILES = 50;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXT_RE = /\.(pdf|docx|txt)$/i;

const UploadResumesTab = ({ onParsed }) => {
  const inputRef = useRef(null);
  const [queue, setQueue] = useState([]); // batch items from API
  const [batchInfo, setBatchInfo] = useState(null);
  const [notice, setNotice] = useState(null);
  const pollTimerRef = useRef(null);
  const reportedCompletedRef = useRef(0);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  const reportCompletedDelta = (items) => {
    const completed = items.filter((it) => it.status === "COMPLETED").length;
    const delta = completed - reportedCompletedRef.current;
    if (delta > 0) {
      reportedCompletedRef.current = completed;
      onParsed?.(delta);
    }
  };

  const startPolling = (id) => {
    stopPolling();

    const tick = async () => {
      try {
        const res = await axiosInstance.get(`${RESUME_API}/batches/${id}`);
        const { batch, items } = res.data || {};
        if (Array.isArray(items)) {
          setQueue(items);
          reportCompletedDelta(items);
        }
        if (batch) setBatchInfo(batch);
        setNotice(null);

        if (batch && batch.status !== "IN_PROGRESS") {
          stopPolling();
          return;
        }
        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        setNotice("Reconnecting…");
        pollTimerRef.current = setTimeout(tick, POLL_RETRY_MS);
      }
    };

    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  };

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter(
      (f) => f.type === "application/pdf" || ACCEPTED_EXT_RE.test(f.name),
    );
    if (!arr.length) return;
    if (arr.length > MAX_FILES) {
      setNotice(`Too many files — max is ${MAX_FILES} per upload.`);
      return;
    }
    const oversized = arr.find((f) => f.size > MAX_FILE_BYTES);
    if (oversized) {
      setNotice(`"${oversized.name}" exceeds the 10 MB per-file limit.`);
      return;
    }
    uploadBatch(arr);
  };

  const uploadBatch = async (files) => {
    stopPolling();
    setNotice(null);
    reportedCompletedRef.current = 0;

    // Optimistic rows so the user sees feedback before the server responds.
    const optimistic = files.map((f, i) => ({
      id: `pending-${i}`,
      fileName: f.name,
      status: "QUEUED",
      errorMessage: null,
      _optimistic: true,
    }));
    setQueue(optimistic);
    setBatchInfo({
      totalCount: files.length,
      completedCount: 0,
      failedCount: 0,
      status: "IN_PROGRESS",
    });

    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const res = await axiosInstance.post(`${RESUME_API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { batchId, items, totalCount } = res.data || {};
      const apiItems = Array.isArray(items) ? items : [];
      setQueue(apiItems);
      setBatchInfo((b) => ({
        ...(b || {}),
        totalCount: totalCount ?? apiItems.length ?? files.length,
        completedCount: 0,
        failedCount: 0,
        status: "IN_PROGRESS",
      }));
      reportCompletedDelta(apiItems);
      if (batchId) startPolling(batchId);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Upload failed";
      setQueue(
        optimistic.map((o) => ({ ...o, status: "FAILED", errorMessage: msg })),
      );
      setBatchInfo((b) => ({
        ...(b || {}),
        status: "PARTIAL",
        failedCount: files.length,
      }));
      setNotice(msg);
    }
  };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        style={{
          border: "2px dashed #C7D2FE",
          borderRadius: 8,
          padding: 24,
          textAlign: "center",
          cursor: "pointer",
          background: "#F8FAFC",
          color: "#475569",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          Drop resumes here or click to choose
        </div>
        <div style={{ fontSize: 11, color: "#94A3B8" }}>
          PDF, DOCX, TXT · up to {MAX_FILES} files, 10 MB each · parsed by AI
          and added to the candidate pool.
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {notice && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#B45309",
            background: "#FEF3C7",
            border: "1px solid #FCD34D",
            borderRadius: 6,
            padding: "6px 10px",
          }}
        >
          {notice}
        </div>
      )}

      {batchInfo && (
        <div
          style={{
            marginTop: 10,
            fontSize: 11,
            color: "#475569",
            display: "flex",
            gap: 12,
          }}
        >
          <span>Total: {batchInfo.totalCount ?? queue.length}</span>
          <span>Completed: {batchInfo.completedCount ?? 0}</span>
          <span>Failed: {batchInfo.failedCount ?? 0}</span>
          <span>Status: {STATUS_LABEL[batchInfo.status] || batchInfo.status}</span>
        </div>
      )}

      {queue.length > 0 && (
        <div
          style={{
            marginTop: 10,
            border: "1px solid #E2E8F0",
            borderRadius: 6,
          }}
        >
          {queue.map((it, i) => (
            <div
              key={it.id ?? `${it.fileName}-${i}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 10px",
                borderBottom:
                  i < queue.length - 1 ? "1px solid #F1F5F9" : "none",
                fontSize: 12,
              }}
            >
              <span
                style={{
                  color: "#0F172A",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {it.fileName}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: STATUS_COLOR[it.status] || "#475569",
                }}
              >
                {STATUS_LABEL[it.status] || it.status}
                {it.errorMessage ? ` — ${it.errorMessage}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const STATUS_LABEL = {
  QUEUED: "Queued",
  IN_PROGRESS: "Parsing…",
  PARSING: "Parsing…",
  COMPLETED: "Parsed",
  FAILED: "Failed",
  PARTIAL: "Partial",
};
const STATUS_COLOR = {
  QUEUED: "#94A3B8",
  IN_PROGRESS: "#7B5AC4",
  PARSING: "#7B5AC4",
  COMPLETED: "#059669",
  FAILED: "#B91C1C",
  PARTIAL: "#B45309",
};

/* Talent pool — list pools, multi-select. */
const TalentPoolTab = ({ jobId, onPickedChange }) => {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = jobId
          ? `/job-posting/talent-pools?jobIds=${jobId}`
          : `/job-posting/talent-pools`;
        const r = await axiosInstance.get(url);
        const list = r.data?.pools || (Array.isArray(r.data) ? r.data : []);
        if (!cancelled) setPools(list);
      } catch {
        if (!cancelled) setPools([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onPickedChange?.(next.size);
      return next;
    });
  };

  if (loading) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "#64748B",
          fontSize: 12,
        }}
      >
        Loading talent pools…
      </div>
    );
  }
  if (pools.length === 0) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          color: "#94A3B8",
          fontSize: 12,
        }}
      >
        No talent pools yet for this job.
      </div>
    );
  }
  return (
    <div
      style={{
        border: "1px solid #E2E8F0",
        borderRadius: 6,
        maxHeight: 240,
        overflowY: "auto",
      }}
    >
      {pools.map((p) => {
        const checked = selected.has(p.id);
        return (
          <label
            key={p.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderBottom: "1px solid #F1F5F9",
              cursor: "pointer",
              background: checked ? "#EEF2FF" : "white",
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => toggle(p.id)}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
                {p.name}
              </div>
              {p.description && (
                <div style={{ fontSize: 11, color: "#64748B" }}>
                  {p.description}
                </div>
              )}
            </div>
            <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
              {p.candidateCount ?? 0} candidates
            </span>
          </label>
        );
      })}
    </div>
  );
};

/* ── step 4: suggested candidates ── */
const CandidatesStep = ({
  loading,
  candidates,
  totalCount,
  allSkills,
  skillFilter,
  toggleSkillFilter,
  search,
  setSearch,
  selectedIds,
  toggleCandidate,
  allFilteredSelected,
  toggleSelectAll,
  sending,
  sendError,
  sendSuccess,
  onCancel,
  onSend,
  pageIndex,
  totalPages,
  pageSize,
  onPrevPage,
  onNextPage,
}) => {
  const selectedCount = selectedIds.size;
  const hasMultiplePages = totalPages > 1;
  const isFirstPage = pageIndex <= 0;
  const isLastPage = totalPages > 0 ? pageIndex >= totalPages - 1 : true;
  const rangeStart = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min(totalCount, (pageIndex + 1) * pageSize);
  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
          Suggested candidates
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
          {loading
            ? "Finding suitable candidates…"
            : `${totalCount} candidate${totalCount === 1 ? "" : "s"} matched. Pick who to invite.`}
        </div>
      </div>

      {/* filters */}
      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          style={input}
        />
        {allSkills.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {allSkills.map((s) => {
              const active = skillFilter.has(s);
              return (
                <button
                  key={s}
                  onClick={() => toggleSkillFilter(s)}
                  style={chip(active)}
                >
                  {s}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* select all bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderRadius: 6,
          background: "#F8FAFC",
          border: "1px solid #E2E8F0",
          marginBottom: 8,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            fontSize: 12,
            color: "#334155",
            fontWeight: 600,
          }}
        >
          <input
            type="checkbox"
            checked={allFilteredSelected}
            onChange={toggleSelectAll}
            disabled={candidates.length === 0}
          />
          Select all {candidates.length ? `(${candidates.length})` : ""}
        </label>
        <span style={{ fontSize: 11, color: "#64748B" }}>
          {selectedCount} selected
        </span>
      </div>

      {/* list */}
      <div
        style={{
          maxHeight: 300,
          overflowY: "auto",
          border: "1px solid #E2E8F0",
          borderRadius: 6,
        }}
      >
        {loading && (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#64748B",
              fontSize: 12,
            }}
          >
            Loading suggestions…
          </div>
        )}
        {!loading && candidates.length === 0 && (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#94A3B8",
              fontSize: 12,
            }}
          >
            No candidates match the current filters.
          </div>
        )}
        {!loading &&
          candidates.map((c) => {
            const checked = selectedIds.has(c.id);
            return (
              <label
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderBottom: "1px solid #F1F5F9",
                  cursor: "pointer",
                  background: checked ? "#EEF2FF" : "white",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCandidate(c.id)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}
                  >
                    {c.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748B" }}>
                    {c.email}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      marginTop: 4,
                    }}
                  >
                    {(c.skills || []).map((s) => (
                      <span
                        key={s}
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: "#F1F5F9",
                          color: "#475569",
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                {typeof c.matchScore === "number" && (
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "#059669" }}
                  >
                    {c.matchScore}% match
                  </span>
                )}
              </label>
            );
          })}
      </div>

      {hasMultiplePages && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
            padding: "8px 4px",
            fontSize: 12,
            color: "#475569",
          }}
        >
          <span>
            Showing {rangeStart}–{rangeEnd} of {totalCount}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={onPrevPage}
              disabled={loading || isFirstPage}
              style={pageBtn(loading || isFirstPage)}
            >
              ← Prev
            </button>
            <span style={{ fontWeight: 600, color: "#334155" }}>
              Page {pageIndex + 1} of {totalPages}
            </span>
            <button
              onClick={onNextPage}
              disabled={loading || isLastPage}
              style={pageBtn(loading || isLastPage)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {sendError && <div style={errorBox}>{sendError}</div>}
      {sendSuccess && <div style={successBox}>{sendSuccess}</div>}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={onCancel} disabled={sending} style={btnGhost}>
          Cancel
        </button>
        <button
          onClick={onSend}
          disabled={sending || selectedCount === 0}
          style={{
            ...btnPrimary,
            marginLeft: "auto",
            opacity: sending || selectedCount === 0 ? 0.5 : 1,
            cursor: sending || selectedCount === 0 ? "not-allowed" : "pointer",
          }}
        >
          {sending
            ? "Sending invites…"
            : `Send invites${selectedCount ? ` (${selectedCount})` : ""}`}
        </button>
      </div>
    </div>
  );
};

/* ── small UI helpers ── */
const Field = ({ label, children }) => (
  <div>
    <div
      style={{
        fontSize: 10,
        color: "#64748B",
        marginBottom: 4,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const Row = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
    {children}
  </div>
);

const Section = ({ heading, items }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        fontSize: 10,
        color: "#94A3B8",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4,
        fontWeight: 700,
      }}
    >
      {heading}
    </div>
    <ul
      style={{
        margin: 0,
        paddingLeft: 18,
        fontSize: 12,
        color: "#334155",
        lineHeight: 1.6,
      }}
    >
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  </div>
);

const chip = (active) => ({
  padding: "5px 12px",
  borderRadius: 100,
  fontSize: 11,
  fontWeight: 600,
  border: active ? "1px solid #7B5AC4" : "1px solid #E2E8F0",
  background: active ? "#EEF2FF" : "white",
  color: active ? "#4338CA" : "#64748B",
  cursor: "pointer",
});

const connectionPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 10px",
  borderRadius: 999,
  border: "1px solid #E2E8F0",
  background: "white",
  fontSize: 11,
  color: "#475569",
};

const bulkActionBar = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "8px 14px",
  background: "#EEF2FF",
  border: "1px solid #C7D2FE",
  borderRadius: 8,
  marginBottom: 10,
};

const avatarStyle = {
  width: 32,
  height: 32,
  borderRadius: 999,
  background: "#F1F5F9",
  color: "#475569",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 700,
  flexShrink: 0,
};

const badgeMuted = {
  fontSize: 10,
  fontWeight: 600,
  padding: "1px 6px",
  borderRadius: 4,
  background: "#F1F5F9",
  color: "#475569",
};

const badgeAmber = {
  fontSize: 10,
  fontWeight: 600,
  padding: "1px 6px",
  borderRadius: 4,
  background: "#FEF3C7",
  color: "#92400E",
};

const badgeIndigo = {
  fontSize: 10,
  fontWeight: 600,
  padding: "1px 6px",
  borderRadius: 4,
  background: "#EEF2FF",
  color: "#4338CA",
};

const subTabStyle = (active) => ({
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 600,
  background: "transparent",
  border: "none",
  borderBottom: active ? "2px solid #7B5AC4" : "2px solid transparent",
  color: active ? "#4338CA" : "#64748B",
  cursor: "pointer",
  marginBottom: -1,
});

const moreOptionsBox = {
  border: "1px dashed #CBD5E1",
  borderRadius: 8,
  padding: "10px 12px",
  background: "#F8FAFC",
};

const moreToggle = {
  background: "transparent",
  border: "none",
  padding: 0,
  fontSize: 12,
  fontWeight: 600,
  color: "#4338CA",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
};

const panelStyle = {
  background: "white",
  borderRadius: 12,
  width: "100%",
  maxWidth: 760,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(15,23,42,0.25)",
};

const hdrStyle = {
  padding: "14px 18px",
  borderBottom: "1px solid #E2E8F0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const bodyStyle = {
  padding: 18,
  overflowY: "auto",
  flex: 1,
};

const cardLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: "#94A3B8",
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const input = {
  width: "100%",
  padding: "8px 12px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  outline: "none",
  background: "white",
  boxSizing: "border-box",
};

const previewBox = {
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  padding: 16,
  maxHeight: 360,
  overflowY: "auto",
};

const btnPrimary = {
  padding: "9px 18px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
};

const btnGhost = {
  padding: "8px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};

const pageBtn = (disabled) => ({
  padding: "5px 10px",
  borderRadius: 6,
  background: "white",
  color: disabled ? "#94A3B8" : "#1E293B",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const errorBox = {
  marginTop: 12,
  padding: "8px 12px",
  borderRadius: 6,
  background: "#FEF2F2",
  color: "#B91C1C",
  fontSize: 12,
};

const successBox = {
  marginTop: 12,
  padding: "8px 12px",
  borderRadius: 6,
  background: "#ECFDF5",
  color: "#047857",
  fontSize: 12,
};

export default CreateJobWizard;
